import React, { useMemo } from 'react'
import { Box, Link, Stack, Typography } from '@mui/material'
import { BriefcaseIcon, BuildingIcon, InfoIcon, PencilIcon, PresentationIcon, Trash2Icon, UsersIcon } from 'lucide-react'
import { EMPTY_VALUE } from '@repo/ui'
import { foundersListStatusLabel, isWonWithoutPaying, SALES_STAGE_LABELS } from '@repo/types'

import { ActionDropdown, EmptyState, Table } from 'components'
import { commercePlatformDisplay, NOT_SET_LABEL } from 'constants/commercePlatforms'
import { sourceChannelLabel } from 'constants/sourceChannels'
import { useLinkedSearch } from 'hooks'
import { useSalesAccounts, useSalesBoard, useSalesLeads } from 'hooks/api/useGtmSalesPipeline'
import type { SalesAccountRow, SalesDealCard, SalesLeadRow } from 'services'

import { shortDate, ToneChip } from '../InternalAnalyticsPage/components/shared'
import { DealMarks, payingNowColumn } from './AutoWonMarks'
import { AnnualValueFigure, PredictedArrFigure, TagMarginFigure } from './DealValueFigures'
import { CREATE_DEMO_TIP, FOUNDERS_LIST_TERM, FOUNDERS_LIST_TONE, LANE_LABEL, LANE_TERM, MANAGE_SALES_TIP, planLabel, sourceGroupLabel, sourceSummary } from './dealsModel'
import { forecastColumns } from './ForecastFigures'
import { companyActivationColumns } from '../CompaniesPage/companyActivationColumns'
import type { SalesWorkspaceActions } from './useSalesWorkspace'

/** VG-27 — the empty state of the Founder's List column, and the name of its "nobody" facet. */
const NOT_ON_LIST_LABEL = 'Not on the list'

const CellText = ({ children, muted }: { children: React.ReactNode; muted?: boolean }) => (
  <Stack justifyContent="center" height="100%" sx={{ minWidth: 0 }}>
    <Typography variant="body3" noWrap color={muted ? 'text.secondary' : 'text.primary'}>
      {children}
    </Typography>
  </Stack>
)

const errorState = (error: unknown, what: string, empty: { title: string; text: string }, icon: React.ReactNode) => {
  const status = (error as (Error & { status?: number }) | null)?.status
  return (
    <EmptyState
      title={status === 403 ? `You cannot view ${what}` : error ? `Could not load ${what}` : empty.title}
      text={
        status === 403
          ? 'GTM → Sales needs the "View GTM" permission and a Vera staff role.'
          : error
            ? 'The request failed. Reload the page, or check the API is running.'
            : empty.text
      }
      icon={icon}
    />
  )
}

/**
 * VRG-727 — a person's stage: their company's current deal (its board lane, or Closed), and Lead
 * for a person with no company. People and Companies both start as Lead (Spencer, 2026-10-20).
 */
export const personStage = (row: Pick<SalesLeadRow, 'account' | 'deal_flags'>): { label: string; term: string | undefined; closed: boolean } => {
  const f = row.account ? row.deal_flags : null
  if (!f) return { label: SALES_STAGE_LABELS.lead, term: LANE_TERM.lead, closed: false }
  if (f.status === 'closed') return { label: `Closed (${f.closed_reason ?? 'closed'})`, term: f.closed_reason === 'lost' ? LANE_TERM.lost : undefined, closed: true }
  return { label: LANE_LABEL[f.lane], term: LANE_TERM[f.lane], closed: false }
}

/**
 * Sales → People (VRG-727; "Leads" until 2026-10-20): every person on the pipeline. Signups
 * arrive on their own; "Add person" adds anyone else. The email is the Access key, so the
 * channel column is Access's own. The tab is `?tab=people`; `?tab=leads` still lands here.
 */
export const SalesPeoplePanel = ({ actions, canManage }: { actions: SalesWorkspaceActions; canManage: boolean }) => {
  const { data, isLoading, error } = useSalesLeads()
  // VRG-712 — opened from a lead journey on one person: their email seeds the search.
  const linkedSearch = useLinkedSearch()
  const columns = useMemo(
    () => [
      {
        field: 'email',
        headerName: 'Person',
        type: 'string',
        width: 260,
        minWidth: 220,
        flex: 260,
        pinned: 'left',
        disableReorder: true,
        renderCell: ({ row }: { row: SalesLeadRow }) => (
          <Stack justifyContent="center" height="100%" sx={{ minWidth: 0 }}>
            <Typography variant="body3" noWrap>
              {row.name ?? row.email}
            </Typography>
            {row.name && (
              <Typography variant="body3" color="text.secondary" noWrap>
                {row.email}
              </Typography>
            )}
          </Stack>
        ),
      },
      {
        // VRG-727 — the company's current deal stage; Lead without a company.
        field: 'stage',
        headerName: 'Stage',
        type: 'string',
        width: 170,
        valueGetter: (_v: unknown, row: SalesLeadRow) => personStage(row).label,
        renderCell: ({ row }: { row: SalesLeadRow }) => {
          const stage = personStage(row)
          return (
            <Stack direction="row" alignItems="center" height="100%" sx={{ minWidth: 0 }}>
              <ToneChip tone={stage.closed ? 'negative' : 'neutral'} label={stage.label} glossaryId={stage.term} />
            </Stack>
          )
        },
      },
      {
        field: 'title',
        headerName: 'Title',
        type: 'string',
        width: 170,
        renderCell: ({ row }: { row: SalesLeadRow }) => <CellText muted={!row.title}>{row.title ?? EMPTY_VALUE}</CellText>,
      },
      {
        field: 'account',
        headerName: 'Company',
        type: 'string',
        width: 200,
        minWidth: 160,
        flex: 200,
        valueGetter: (_v: unknown, row: SalesLeadRow) => row.account?.name ?? '',
        renderCell: ({ row }: { row: SalesLeadRow }) =>
          row.account ? (
            <Stack direction="row" alignItems="center" spacing={1.5} height="100%" sx={{ minWidth: 0 }}>
              <Link component="button" variant="body3" onClick={() => actions.openCompany(row.account!.id)} sx={{ textAlign: 'left', minWidth: 0 }} noWrap>
                {row.account.name}
              </Link>
              {/* VRG-700 — the company's deal marks, the same as its board card. */}
              <DealMarks wonWithoutPaying={!!row.deal_flags?.won_without_paying} autoWon={row.deal_flags?.auto_won} />
            </Stack>
          ) : (
            <CellText muted>No company</CellText>
          ),
      },
      {
        field: 'person_channel',
        headerName: 'Source channel',
        type: 'string',
        width: 180,
        valueGetter: (_v: unknown, row: SalesLeadRow) => sourceChannelLabel(row.person_channel),
        renderCell: ({ row }: { row: SalesLeadRow }) => <CellText muted={!row.person_channel}>{sourceChannelLabel(row.person_channel)}</CellText>,
      },
      // VRG-692 — the person's company's figures (a person without a company has none).
      ...forecastColumns<SalesLeadRow>((row) => row.forecast),
      {
        field: 'signup',
        // VRG-727 — "Signed up" is a STAGE now (the company has a platform account); this
        // column is the waitlist signup.
        headerName: 'Joined waitlist',
        type: 'string',
        width: 150,
        valueGetter: (_v: unknown, row: SalesLeadRow) => row.signup?.created_at ?? '',
        renderCell: ({ row }: { row: SalesLeadRow }) =>
          row.signup ? (
            <CellText>
              {shortDate(row.signup.created_at)}
              {row.signup.granted_at ? ' · granted' : ''}
            </CellText>
          ) : (
            <CellText muted>Added by hand</CellText>
          ),
      },
      {
        field: 'actions',
        headerName: '',
        width: 54,
        sortable: false,
        filterable: false,
        disableColumnMenu: true,
        disableReorder: true,
        align: 'center',
        pinned: 'right',
        renderCell: ({ row }: { row: SalesLeadRow }) => (
          <ActionDropdown
            items={[
              { label: 'Edit person', icon: <PencilIcon strokeWidth={1} />, disabled: !canManage, tooltip: canManage ? undefined : MANAGE_SALES_TIP, onClick: () => actions.editLead(row) },
              { label: 'Open company', icon: <InfoIcon strokeWidth={1} />, visible: !!row.account, onClick: () => row.account && actions.openCompany(row.account.id) },
              { label: 'Remove person', icon: <Trash2Icon strokeWidth={1} />, destructive: true, divider: true, disabled: !canManage, tooltip: canManage ? undefined : MANAGE_SALES_TIP, onClick: () => actions.removeLead(row) },
            ]}
          />
        ),
      },
    ],
    [actions, canManage],
  )
  return (
    <Table
      loading={isLoading}
      columns={columns}
      rows={data ?? []}
      alwaysShowRowSelection={false}
      fillBottom
      paginationMode="client"
      initialPageSize={50}
      viewKey="internal-gtm-sales-leads"
      search={linkedSearch}
      emptyState={errorState(error, 'people', { title: 'No people yet', text: 'Waitlist signups appear here on their own. Add anyone else with Add person.' }, <UsersIcon strokeWidth={1} size={32} />)}
    />
  )
}

/**
 * Sales → Companies: the brands on the pipeline. NOT Accounts → Companies (platform tenants):
 * a company here links to its platform company once one exists.
 */
export const SalesCompaniesPanel = ({
  actions,
  canManage,
  canCreateDemo,
}: {
  actions: SalesWorkspaceActions
  canManage: boolean
  /** VRG-723 — Create demo is gated on the Demos permission. */
  canCreateDemo: boolean
}) => {
  const { data, isLoading, error } = useSalesAccounts()
  const columns = useMemo(
    () => [
      {
        field: 'name',
        headerName: 'Company',
        type: 'string',
        width: 220,
        minWidth: 180,
        flex: 220,
        pinned: 'left',
        disableReorder: true,
        renderCell: ({ row }: { row: SalesAccountRow }) => (
          <Stack justifyContent="center" height="100%" sx={{ minWidth: 0 }}>
            <Link component="button" variant="body3" onClick={() => actions.openCompany(row.id)} sx={{ textAlign: 'left' }} noWrap>
              {row.name}
            </Link>
            {row.domain && (
              <Typography variant="body3" color="text.secondary" noWrap>
                {row.domain}
              </Typography>
            )}
          </Stack>
        ),
      },
      {
        field: 'stage',
        headerName: 'Stage',
        type: 'string',
        width: 170,
        valueGetter: (_v: unknown, row: SalesAccountRow) => (row.deal ? (row.deal.status === 'closed' ? 'Closed' : LANE_LABEL[row.deal.lane]) : ''),
        renderCell: ({ row }: { row: SalesAccountRow }) => (
          <Stack direction="row" alignItems="center" spacing={1.5} height="100%" sx={{ minWidth: 0 }}>
            <CellText muted={!row.deal || row.deal.status === 'closed'}>
              {row.deal ? (row.deal.status === 'closed' ? `Closed (${row.deal.closed_reason})` : LANE_LABEL[row.deal.lane]) : EMPTY_VALUE}
            </CellText>
            {/* VRG-700 — the deal's marks, the same as its board card. */}
            {row.deal && <DealMarks wonWithoutPaying={isWonWithoutPaying(row.deal)} autoWon={row.deal.auto_won} />}
          </Stack>
        ),
      },
      payingNowColumn<SalesAccountRow>((row) => row.deal?.paying_now),
      {
        /*
         * VG-27 — the Founder's List pill. `multiSelect`, so the Filters drawer offers it as a
         * facet (like Commerce platform): THAT is how "how full is the list" gets answered — the
         * programme's cap is Spencer's own calendar and nothing in the CRM showed it. Filtering to
         * Founding member + Member counts the seats in use; Invited and Alumni are deliberately
         * separate facets, since neither costs calendar.
         *
         * The facet is the LABEL, and "Not on the list" is a facet of its own — the common case,
         * and the one you filter AWAY.
         */
        field: 'founders_list_status',
        headerName: "Founder's List",
        type: 'multiSelect',
        // Sized for the header, not the values (§49): "Founder's List" plus its sort icon needs
        // more than the widest pill ("Founding member") does.
        width: 190,
        valueGetter: (_v: unknown, row: SalesAccountRow) => foundersListStatusLabel(row.founders_list_status) || NOT_ON_LIST_LABEL,
        renderCell: ({ row }: { row: SalesAccountRow }) =>
          row.founders_list_status ? (
            <Stack direction="row" alignItems="center" height="100%" sx={{ minWidth: 0 }}>
              <ToneChip
                tone={FOUNDERS_LIST_TONE[row.founders_list_status]}
                label={foundersListStatusLabel(row.founders_list_status)}
                glossaryId={FOUNDERS_LIST_TERM[row.founders_list_status]}
              />
            </Stack>
          ) : (
            <CellText muted>{NOT_ON_LIST_LABEL}</CellText>
          ),
      },
      {
        // VRG-701 — a company can have several deals; the Stage column shows its primary one.
        field: 'deals',
        headerName: 'Deals',
        type: 'number',
        headerAlign: 'left',
        align: 'left',
        width: 120,
        valueGetter: (_v: unknown, row: SalesAccountRow) => row.deals.length,
        renderCell: ({ row }: { row: SalesAccountRow }) => {
          const open = row.deals.filter((d) => d.status === 'open').length
          return (
            <CellText muted={row.deals.length === 0}>
              {row.deals.length}
              {row.deals.length > 1 ? ` · ${open} open` : ''}
            </CellText>
          )
        },
      },
      // VRG-692 — predicted vs actual, for Vera (ARR) and for the brand (ROI), with accuracy.
      ...forecastColumns<SalesAccountRow>((row) => row.deal?.forecast),
      {
        field: 'source',
        headerName: 'Lead source',
        type: 'string',
        width: 240,
        minWidth: 200,
        flex: 240,
        valueGetter: (_v: unknown, row: SalesAccountRow) => sourceGroupLabel(row.source.group),
        renderCell: ({ row }: { row: SalesAccountRow }) => (
          <Stack direction="row" alignItems="center" spacing={2} height="100%" sx={{ minWidth: 0 }}>
            <ToneChip tone="neutral" label={sourceGroupLabel(row.source.group)} />
            <Typography variant="body3" color="text.secondary" noWrap title={sourceSummary(row.source)}>
              {row.source.detail ?? ''}
            </Typography>
          </Stack>
        ),
      },
      {
        field: 'lead_count',
        headerName: 'People',
        type: 'number',
        headerAlign: 'left',
        align: 'left',
        width: 100,
        renderCell: ({ row }: { row: SalesAccountRow }) => <CellText>{row.lead_count}</CellText>,
      },
      {
        // VRG-716 — multiSelect, so the Filters drawer offers it as a facet (the Companies filter).
        // The facet is the LABEL, and "Not set" is a facet of its own: finding the companies nobody
        // has looked at is half of why the filter exists.
        field: 'commerce_platform',
        headerName: 'Commerce platform',
        type: 'multiSelect',
        width: 200,
        valueGetter: (_v: unknown, row: SalesAccountRow) => commercePlatformDisplay(row.commerce_platform),
        renderCell: ({ row }: { row: SalesAccountRow }) =>
          row.commerce_platform ? (
            <Stack direction="row" alignItems="center" height="100%" sx={{ minWidth: 0 }}>
              <ToneChip tone="neutral" label={commercePlatformDisplay(row.commerce_platform)} />
            </Stack>
          ) : (
            <CellText muted>{NOT_SET_LABEL}</CellText>
          ),
      },
      {
        field: 'company',
        headerName: 'Platform company',
        type: 'string',
        width: 190,
        valueGetter: (_v: unknown, row: SalesAccountRow) => row.company?.name ?? '',
        renderCell: ({ row }: { row: SalesAccountRow }) => <CellText muted={!row.company}>{row.company?.name ?? 'Not yet'}</CellText>,
      },
      // VRG-718 — the linked platform company's Activated milestone and activations (read-only).
      ...companyActivationColumns<SalesAccountRow>((row) => row.activation),
      {
        field: 'owner',
        headerName: 'Owner',
        type: 'string',
        width: 150,
        valueGetter: (_v: unknown, row: SalesAccountRow) => row.deal?.owner?.name ?? row.owner?.name ?? '',
        renderCell: ({ row }: { row: SalesAccountRow }) => (
          <CellText muted={!(row.deal?.owner ?? row.owner)}>{row.deal?.owner?.name ?? row.owner?.name ?? 'Nobody'}</CellText>
        ),
      },
      {
        field: 'actions',
        headerName: '',
        width: 54,
        sortable: false,
        filterable: false,
        disableColumnMenu: true,
        disableReorder: true,
        align: 'center',
        pinned: 'right',
        renderCell: ({ row }: { row: SalesAccountRow }) => (
          <ActionDropdown
            items={[
              { label: 'Open company', icon: <BuildingIcon strokeWidth={1} />, onClick: () => actions.openCompany(row.id) },
              { label: 'Add deal', icon: <BriefcaseIcon strokeWidth={1} />, disabled: !canManage, tooltip: canManage ? undefined : MANAGE_SALES_TIP, onClick: () => actions.addDeal(row.id) },
              { label: 'Add person', icon: <UsersIcon strokeWidth={1} />, disabled: !canManage, tooltip: canManage ? undefined : MANAGE_SALES_TIP, onClick: () => actions.addLead(row.id) },
              { label: 'Create demo', icon: <PresentationIcon strokeWidth={1} />, disabled: !canCreateDemo, tooltip: canCreateDemo ? undefined : CREATE_DEMO_TIP, onClick: () => actions.createDemo(row.id) },
            ]}
          />
        ),
      },
    ],
    [actions, canManage, canCreateDemo],
  )
  return (
    <Box>
      <Table
        loading={isLoading}
        columns={columns}
        rows={data ?? []}
        alwaysShowRowSelection={false}
        fillBottom
        paginationMode="client"
        initialPageSize={50}
        viewKey="internal-gtm-sales-companies"
        emptyState={errorState(
          error,
          'companies',
          { title: 'No companies yet', text: 'Companies come in from waitlist signups, demos and platform companies. Add a brand with Add company.' },
          <BuildingIcon strokeWidth={1} size={32} />,
        )}
      />
    </Box>
  )
}

/**
 * VRG-701 — Sales → Deals: every deal, open and closed. A company can have several (an upsell, a
 * pilot → rollout), so this is the one list where each is its own row. Reads the board's query
 * (the same snapshot the board and tiles fold), so the two can never disagree.
 */
export const SalesDealsPanel = ({ actions, canManage }: { actions: SalesWorkspaceActions; canManage: boolean }) => {
  const { data, isLoading, error } = useSalesBoard(null)
  const columns = useMemo(
    () => [
      {
        field: 'account_name',
        headerName: 'Deal',
        type: 'string',
        width: 260,
        minWidth: 220,
        flex: 260,
        pinned: 'left',
        disableReorder: true,
        renderCell: ({ row }: { row: SalesDealCard }) => (
          <Stack justifyContent="center" height="100%" sx={{ minWidth: 0 }}>
            <Link component="button" variant="body3" onClick={() => actions.openCompany(row.account_id, row.id)} sx={{ textAlign: 'left' }} noWrap>
              {row.account_name}
            </Link>
            <Typography variant="body3" color="text.secondary" noWrap>
              {row.name ?? (row.is_first_deal ? 'First deal' : `Opened ${shortDate(row.opened_at)}`)}
            </Typography>
          </Stack>
        ),
      },
      {
        field: 'stage',
        headerName: 'Stage',
        type: 'string',
        width: 180,
        valueGetter: (_v: unknown, row: SalesDealCard) => (row.status === 'closed' ? `Closed (${row.closed_reason})` : LANE_LABEL[row.lane]),
        renderCell: ({ row }: { row: SalesDealCard }) => (
          <Stack direction="row" alignItems="center" spacing={1.5} height="100%" sx={{ minWidth: 0 }}>
            <CellText muted={row.status === 'closed'}>{row.status === 'closed' ? `Closed (${row.closed_reason})` : LANE_LABEL[row.lane]}</CellText>
            <DealMarks wonWithoutPaying={isWonWithoutPaying(row)} autoWon={row.auto_won} />
          </Stack>
        ),
      },
      payingNowColumn<SalesDealCard>((row) => row.paying_now),
      // VRG-736 — the three value figures, each with its formula tooltip. Header widths fit the
      // label + sort icon (§49). Numbers, so they sort and can be grouped later.
      {
        field: 'value',
        headerName: 'Predicted ARR',
        type: 'number',
        headerAlign: 'left',
        align: 'left',
        width: 170,
        valueGetter: (_v: unknown, row: SalesDealCard) => row.value.amount ?? -1,
        renderCell: ({ row }: { row: SalesDealCard }) => (
          <Stack justifyContent="center" height="100%" sx={{ minWidth: 0 }}>
            <PredictedArrFigure deal={row} size="compact" />
          </Stack>
        ),
      },
      {
        field: 'tag_margin',
        headerName: 'Tag margin',
        type: 'number',
        headerAlign: 'left',
        align: 'left',
        width: 150,
        valueGetter: (_v: unknown, row: SalesDealCard) => row.tag_margin ?? -1,
        renderCell: ({ row }: { row: SalesDealCard }) => (
          <Stack justifyContent="center" height="100%" sx={{ minWidth: 0 }}>
            <TagMarginFigure deal={row} size="compact" />
          </Stack>
        ),
      },
      {
        field: 'predicted_annual_value',
        headerName: 'Annual value',
        type: 'number',
        headerAlign: 'left',
        align: 'left',
        width: 160,
        valueGetter: (_v: unknown, row: SalesDealCard) => row.predicted_annual_value ?? -1,
        renderCell: ({ row }: { row: SalesDealCard }) => (
          <Stack justifyContent="center" height="100%" sx={{ minWidth: 0 }}>
            <AnnualValueFigure deal={row} size="compact" />
          </Stack>
        ),
      },
      {
        field: 'plan',
        headerName: 'Plan',
        type: 'string',
        width: 150,
        valueGetter: (_v: unknown, row: SalesDealCard) => planLabel(row.plan),
        renderCell: ({ row }: { row: SalesDealCard }) => (
          <Stack direction="row" alignItems="center" spacing={2} height="100%" sx={{ minWidth: 0 }}>
            <CellText muted={!row.plan}>{planLabel(row.plan)}</CellText>
            {row.plan_source === 'stripe' && <ToneChip tone="positive" label="Stripe" />}
          </Stack>
        ),
      },
      {
        field: 'source',
        headerName: 'Source',
        type: 'string',
        width: 220,
        valueGetter: (_v: unknown, row: SalesDealCard) => sourceGroupLabel(row.source.group),
        renderCell: ({ row }: { row: SalesDealCard }) => (
          <Stack direction="row" alignItems="center" spacing={2} height="100%" sx={{ minWidth: 0 }}>
            <ToneChip tone={row.source.group === 'expansion' ? 'info' : 'neutral'} label={sourceGroupLabel(row.source.group)} />
            <Typography variant="body3" color="text.secondary" noWrap title={sourceSummary(row.source)}>
              {row.source.detail ?? ''}
            </Typography>
          </Stack>
        ),
      },
      {
        field: 'owner',
        headerName: 'Owner',
        type: 'string',
        width: 150,
        valueGetter: (_v: unknown, row: SalesDealCard) => row.owner?.name ?? '',
        renderCell: ({ row }: { row: SalesDealCard }) => <CellText muted={!row.owner}>{row.owner?.name ?? 'Nobody'}</CellText>,
      },
      {
        field: 'expected_close_on',
        headerName: 'Expected close',
        type: 'string',
        width: 160,
        renderCell: ({ row }: { row: SalesDealCard }) => (
          <CellText muted={!row.expected_close_on}>{row.expected_close_on ? shortDate(`${row.expected_close_on}T12:00:00`) : EMPTY_VALUE}</CellText>
        ),
      },
      {
        field: 'opened_at',
        headerName: 'Opened',
        type: 'string',
        width: 130,
        renderCell: ({ row }: { row: SalesDealCard }) => <CellText>{shortDate(row.opened_at)}</CellText>,
      },
      {
        field: 'actions',
        headerName: '',
        width: 54,
        sortable: false,
        filterable: false,
        disableColumnMenu: true,
        disableReorder: true,
        align: 'center',
        pinned: 'right',
        renderCell: ({ row }: { row: SalesDealCard }) => (
          <ActionDropdown
            items={[
              { label: 'Open deal', icon: <InfoIcon strokeWidth={1} />, onClick: () => actions.openCompany(row.account_id, row.id) },
              { label: 'Add deal at this company', icon: <BriefcaseIcon strokeWidth={1} />, disabled: !canManage, tooltip: canManage ? undefined : MANAGE_SALES_TIP, onClick: () => actions.addDeal(row.account_id) },
            ]}
          />
        ),
      },
    ],
    [actions, canManage],
  )
  return (
    <Table
      loading={isLoading}
      columns={columns}
      rows={data?.deals ?? []}
      alwaysShowRowSelection={false}
      fillBottom
      paginationMode="client"
      initialPageSize={50}
      viewKey="internal-gtm-sales-deals"
      emptyState={errorState(error, 'deals', { title: 'No deals yet', text: 'A company gets its first deal on its own. Add another with Add deal.' }, <BriefcaseIcon strokeWidth={1} size={32} />)}
    />
  )
}
